import { DbLead, getScoreLabel } from "@/lib/types";
import { Flame, Info } from "lucide-react";

/**
 * Lead score summary popover.
 *
 * Scoring logic lives exclusively in the Postgres function
 * `compute_lead_score()`. This component only DISPLAYS the score from the DB —
 * it does not recompute factors locally, since any client-side recompute would
 * silently drift away from the authoritative server value.
 *
 * The full per-factor breakdown will return as a server-side "explain" RPC in
 * a future phase.
 */
export default function LeadScoreBreakdown({ lead }: { lead: DbLead }) {
  const score = lead.score ?? 0;
  const label = getScoreLabel(score);

  return (
    <div className="w-64 p-3 space-y-2.5">
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-1.5">
          {score >= 85 && <Flame className="h-4 w-4 text-hot" />}
          <span className="text-sm font-bold text-navy">Lead score</span>
        </div>
        <span className="text-sm font-bold text-navy tabular-nums">{score}/100</span>
      </div>

      <div className="text-xs space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Tier</span>
          <span className="font-semibold text-navy">{label}</span>
        </div>
        <div className="flex items-start gap-1.5 rounded-md bg-muted/40 p-2">
          <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-[11px] leading-snug text-muted-foreground">
            Scores are computed server-side from your service areas, specialty
            preferences, the lead's urgency &amp; hours, payer source, and any AI
            enrichment signals. Update those settings to influence future scoring.
          </p>
        </div>
      </div>
    </div>
  );
}
